import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TID } from "@/constants/testIds";
import { formatApiError } from "@/lib/api";
import { ShieldCheck, Lock } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e?.preventDefault?.();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      nav("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen w-full grid md:grid-cols-2 bg-[#F8FAFC]">
      {/* Left panel */}
      <div className="hidden md:flex flex-col justify-between p-12 sidebar-dark relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-sams-primary/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-purple-500/10 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-sams-primary grid place-items-center text-white font-black">S</div>
            <div>
              <div className="text-white font-heading font-extrabold text-xl leading-none">SAMS</div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 mt-1">Supplier Audit Management System</div>
            </div>
          </div>
        </div>
        <div className="relative space-y-6">
          <h1 className="font-heading text-4xl font-extrabold text-white leading-tight">
            Digital supplier audits<br />with precision and speed.
          </h1>
          <p className="text-slate-300 max-w-md leading-relaxed">
            Plan, execute, review and close supplier audits in one unified enterprise workspace. Track KPIs, findings, and CAPA verification across every plant.
          </p>
          <div className="grid grid-cols-2 gap-4 pt-4 max-w-md">
            <div className="border border-white/10 rounded-md p-4 bg-white/5">
              <div className="text-3xl font-heading font-black text-white">100%</div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">Paperless</div>
            </div>
            <div className="border border-white/10 rounded-md p-4 bg-white/5">
              <div className="text-3xl font-heading font-black text-white">A-D</div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">Grade System</div>
            </div>
          </div>
        </div>
        <div className="relative text-xs text-slate-400 flex items-center gap-2">
          <ShieldCheck size={14} /> Enterprise-grade access control & audit trail
        </div>
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-md">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Sign in</div>
            <h2 className="font-heading text-3xl font-extrabold text-slate-900 mt-2">Welcome back</h2>
            <p className="text-sm text-slate-600 mt-1">Enter your credentials to access the audit console.</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
              <Input
                id="email" type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid={TID.login.emailInput}
                className="mt-1 h-11 rounded-md"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password</Label>
              <Input
                id="password" type="password" autoComplete="current-password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid={TID.login.passwordInput}
                className="mt-1 h-11 rounded-md"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div data-testid={TID.login.error} className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit" disabled={loading}
              data-testid={TID.login.submit}
              className="w-full h-11 bg-sams-primary hover:bg-sams-primaryHover text-white rounded-md font-semibold transition-colors"
            >
              <Lock size={14} className="mr-2" />
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
